// DeferredActionDetails.jsx
import React, {useEffect, useMemo, useState} from "react";
import {
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    CircularProgress,
    Divider,
    Grid,
    IconButton,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TextField,
    Tooltip,
    Typography,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions, Switch,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import EditIcon from "@mui/icons-material/Edit";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import {
    cancelDeferredActionDoLater,
    createDeferredActionDoLater,
    deferredActionsRunById,
    getDeferredActionById, updateDeferredActionInterval,
    getDeferredActionDoLater,
    getDeferredActionLogs,
    getDeferredActionRunLogs,
    deleteDeferredAction,
    updateDeferredActionParamValue,
    updateDeferredActionStatus
} from "../api/AiAgentApi";
import DeferredActionLogList from "./DeferredActionLogList";
import {useNavigate} from "react-router-dom";
import {IntervalMinutesField} from "./IntervalMinutesField";

// ===== Утилиты =====
const fmtDate = (iso) => {
    try {
        return new Date(iso).toLocaleString();
    } catch {
        return iso;
    }
};
const parseDateMs = (iso) => {
    if (!iso) return null;
    const t = Date.parse(iso);
    return Number.isFinite(t) ? t : null;
};
const fmtDurationMs = (ms) => {
    if (ms == null || !Number.isFinite(ms)) return "—";
    const v = Math.max(0, Math.round(ms));
    if (v < 1000) return `${v}мс`;
    const totalSeconds = Math.floor(v / 1000);
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;
    if (hours > 0) return `${hours}ч ${String(minutes).padStart(2, "0")}м ${String(seconds).padStart(2, "0")}с`;
    if (minutes > 0) return `${minutes}м ${String(seconds).padStart(2, "0")}с`;
    return `${seconds}с`;
};
const getRunLogDurationMs = (runLog) => {
    const direct = runLog?.durationMs;
    if (typeof direct === "number" && Number.isFinite(direct)) return direct;
    const createdAtMs = parseDateMs(runLog?.createdAt);
    if (createdAtMs == null) return null;
    const finishedAtMs = parseDateMs(runLog?.finishedAt);
    const endMs = finishedAtMs != null ? finishedAtMs : Date.now();
    return Math.max(0, endMs - createdAtMs);
};
const fmtDateMsk = (iso) => {
    try {
        const date = new Date(iso);
        const value = new Intl.DateTimeFormat("ru-RU", {
            timeZone: "Europe/Moscow",
            year: "numeric",
            month: "2-digit",
            day: "2-digit",
            hour: "2-digit",
            minute: "2-digit",
            second: "2-digit",
            hour12: false,
        }).format(date);
        return `${value} МСК`;
    } catch {
        return iso;
    }
};
const SOURCE_COLORS = {EXTERNAL: "primary", INTERNAL: "default"};

// Показ значений вида { var: "param" } красиво
function VarView({value}) {
    if (value && typeof value === "object" && "var" in value) {
        return <code>{`{ var: "${value.var}" }`}</code>;
    }
    return <code>{JSON.stringify(value)}</code>;
}

// Упрощённый читаемый вывод JSONLogic
function ConditionReadableView({condition}) {
    let obj = condition;
    if (typeof condition === "string") {
        try {
            obj = JSON.parse(condition);
        } catch {
            return <Typography color="error">Некорректный JSON</Typography>;
        }
    }
    const text = toReadable(obj);
    return (
        <Box sx={{
            fontFamily: "monospace",
            whiteSpace: "pre-wrap",
            p: 1,
            borderRadius: 1,
            bgcolor: "rgba(255,255,255,0.03)",
            border: "1px solid rgba(255,255,255,0.1)"
        }}>
            <Typography variant="body2" sx={{lineHeight: 1.6}}>{text}</Typography>
        </Box>
    );
}

function toReadable(node) {
    if (!node || typeof node !== "object") return String(node);
    const opKeys = Object.keys(node);
    if (opKeys.length === 0) return "";
    const op = opKeys[0];
    const args = node[op];
    switch (op) {
        case "and":
        case "or": {
            const joined = (args || []).map(toReadable).filter(Boolean);
            const symbol = op === "and" ? "&&" : "||";
            return "(" + joined.join(` ${symbol} `) + ")";
        }
        case "==":
        case "!=":
        case "===":
        case "!==":
        case ">":
        case "<":
        case ">=":
        case "<=":
            return `(${toReadable(args[0])} ${op} ${toReadable(args[1])})`;
        case "!":
            return "!(" + toReadable(args[0]) + ")";
        case "var":
            return args; // имя переменной
        default:
            if (Array.isArray(args)) return `${op}(${args.map(toReadable).join(", ")})`;
            return `${op} ${toReadable(args)}`;
    }
}

// Хук для диалога редактирования
function useEditParamDialog(initial = null) {
    const [open, setOpen] = useState(false);
    const [editing, setEditing] = useState(initial); // { rowId, scriptParamId, name, value }
    const [value, setValue] = useState("");

    const openDialog = (payload) => {
        setEditing(payload);
        setValue(payload?.value ?? "");
        setOpen(true);
    };
    const closeDialog = () => setOpen(false);

    return {open, editing, value, setValue, openDialog, closeDialog};
}

export default function DeferredActionDetails() {
    const navigate = useNavigate()

    const OVERALL_STATUS = {
        NOT_STARTED: {label: "Не запущен", color: "default", variant: "outlined"},
        IN_PROGRESS: {label: "В процессе", color: "warning", variant: "filled"},
    };

    // id из query-string: ?id=...
    const [id, setId] = useState(() => new URLSearchParams(window.location.search).get("id"));
    const [loading, setLoading] = useState(false);
    const [loadingSend, setLoadingSend] = useState(false);
    const [item, setItem] = useState(null);
    const [error, setError] = useState("");
    const editDlg = useEditParamDialog();
    const [logsRefreshing, setLogsRefreshing] = useState(false);
    const [logsLoading, setLogsLoading] = useState(false);
    const [, setLogs] = useState([]);
    const LOG_PAGE_SIZE = 50;
    const [runLogsLoading, setRunLogsLoading] = useState(false);
    const [runLogs, setRunLogs] = useState([]);
    const [runLogsPage, setRunLogsPage] = useState(0);
    const [runLogsHasNext, setRunLogsHasNext] = useState(false);
    const RUN_LOG_PAGE_SIZE = 20;
    const [doLaterLoading, setDoLaterLoading] = useState(false);
    const [doLaterItems, setDoLaterItems] = useState([]);
    const [doLaterPage, setDoLaterPage] = useState(0);
    const [doLaterHasNext, setDoLaterHasNext] = useState(false);
    const DO_LATER_PAGE_SIZE = 20;
    const [doLaterAtLocal, setDoLaterAtLocal] = useState("");
    const [doLaterSubmitting, setDoLaterSubmitting] = useState(false);
    const [sectionsOpen, setSectionsOpen] = useState({
        doLater: false,
        scriptParams: false,
        externalParams: false,
        condition: false,
        templateBody: false,
    });
    const [selectedRunLogId, setSelectedRunLogId] = useState(null);
    const [selectedRunLogsLoading, setSelectedRunLogsLoading] = useState(false);
    const [selectedRunLogs, setSelectedRunLogs] = useState([]);
    const [selectedRunLogsPage, setSelectedRunLogsPage] = useState(0);
    const [selectedRunLogsHasNext, setSelectedRunLogsHasNext] = useState(false);
    const [selectedRunLogsCounts, setSelectedRunLogsCounts] = useState(null);
    const [selectedRunLogsErrorTypeCounts, setSelectedRunLogsErrorTypeCounts] = useState(null);
    const [selectedRunLogsTotal, setSelectedRunLogsTotal] = useState(0);
    const SELECTED_RUN_LOGS_PAGE_SIZE = 50;

    useEffect(() => {
        const handler = () => setId(new URLSearchParams(window.location.search).get("id"));
        window.addEventListener("popstate", handler);
        return () => window.removeEventListener("popstate", handler);
    }, []);

    useEffect(() => {
        let mounted = true;
        (async () => {
            if (!id) return;
            setLoading(true);
            setError("");
            setItem(null);
            try {
                const data = await getDeferredActionById(id);
                if (mounted) setItem(data || null);
            } catch (e) {
                if (mounted) setError("Не удалось загрузить отложенное действие");
            } finally {
                if (mounted) setLoading(false);
            }
        })();
        return () => {
            mounted = false;
        };
    }, [id]);

    const isInProgress = (item?.overallStatus || "NOT_STARTED") === "IN_PROGRESS";

    const fetchLogsPage = async (page, {append} = {append: false}) => {
        if (!id) return;
        const p = Math.max(0, page);
        const size = LOG_PAGE_SIZE;
        setLogsLoading(true);

        try {
            const data = await getDeferredActionLogs(id, p, size);
            const nextLogs = Array.isArray(data?.logs) ? data.logs : [];
            setLogs((prev) => (append ? [...prev, ...nextLogs] : nextLogs));
        } finally {
            setLogsLoading(false);
        }
    };

    const fetchRunLogsPage = async (page, {append} = {append: false}) => {
        if (!id) return;
        const p = Math.max(0, page);
        const size = RUN_LOG_PAGE_SIZE;
        setRunLogsLoading(true);
        try {
            const data = await getDeferredActionRunLogs(id, p, size);
            const nextLogs = Array.isArray(data?.logs) ? data.logs : [];
            setRunLogsHasNext(!!data?.hasNext);
            setRunLogsPage(p);
            setRunLogs((prev) => (append ? [...prev, ...nextLogs] : nextLogs));
        } finally {
            setRunLogsLoading(false);
        }
    };

    const fetchDoLaterPage = async (page, {append} = {append: false}) => {
        if (!id) return;
        const p = Math.max(0, page);
        setDoLaterLoading(true);
        try {
            const data = await getDeferredActionDoLater(id, p, DO_LATER_PAGE_SIZE);
            const nextItems = Array.isArray(data?.items) ? data.items : [];
            setDoLaterHasNext(!!data?.hasNext);
            setDoLaterPage(p);
            setDoLaterItems((prev) => (append ? [...prev, ...nextItems] : nextItems));
        } finally {
            setDoLaterLoading(false);
        }
    };

    const fetchSelectedRunLogsPage = async (runLogId, page, {append} = {append: false}) => {
        if (!id || !runLogId) return;
        setSelectedRunLogsLoading(true);
        try {
            const p = Math.max(0, page);
            const data = await getDeferredActionLogs(id, p, SELECTED_RUN_LOGS_PAGE_SIZE, runLogId);
            const matched = Array.isArray(data?.logs) ? data.logs : [];
            setSelectedRunLogsHasNext(!!data?.hasNext);
            setSelectedRunLogsCounts(data?.statusCounts || null);
            setSelectedRunLogsErrorTypeCounts(data?.errorTypeCounts || null);
            setSelectedRunLogsTotal(Number(data?.total || 0));
            setSelectedRunLogsPage(p);
            setSelectedRunLogs((prev) => (append ? [...prev, ...matched] : matched));
        } finally {
            setSelectedRunLogsLoading(false);
        }
    };

	    const handleSelectRunLog = async (runLogId) => {
	        if (!runLogId) return;
	        if (selectedRunLogId === runLogId) {
	            setSelectedRunLogId(null);
            setSelectedRunLogs([]);
            setSelectedRunLogsPage(0);
            setSelectedRunLogsHasNext(false);
            setSelectedRunLogsCounts(null);
            setSelectedRunLogsErrorTypeCounts(null);
            setSelectedRunLogsTotal(0);
            return;
        }
        setSelectedRunLogId(runLogId);
        setSelectedRunLogs([]);
        setSelectedRunLogsPage(0);
        setSelectedRunLogsHasNext(false);
        setSelectedRunLogsCounts(null);
	        setSelectedRunLogsErrorTypeCounts(null);
	        setSelectedRunLogsTotal(0);
	        await fetchSelectedRunLogsPage(runLogId, 0, {append: false});
	    };

	    const selectedRunLog = useMemo(() => {
	        if (!selectedRunLogId) return null;
	        return (runLogs || []).find((r) => String(r?.id) === String(selectedRunLogId)) || null;
	    }, [runLogs, selectedRunLogId]);

	    const RunLogStatusChip = ({runLog}) => {
	        const status = runLog?.status;
	        const meta = status ? RUN_LOG_STATUS_META[status] : null;
	        const label = meta?.label || status || "—";
	        const duration = fmtDurationMs(getRunLogDurationMs(runLog));

	        const tooltip = (
	            <Box>
	                {status && <Typography variant="caption" sx={{display: "block"}}>Статус: {status}</Typography>}
	                {runLog?.resumedAt && (
	                    <Typography variant="caption" sx={{display: "block"}}>Возобновлено: {fmtDate(runLog.resumedAt)}</Typography>
	                )}
	                {runLog?.finishedAt && (
	                    <Typography variant="caption" sx={{display: "block"}}>Завершено: {fmtDate(runLog.finishedAt)}</Typography>
	                )}
	                {duration !== "—" && (
	                    <Typography variant="caption" sx={{display: "block"}}>Длительность: {duration}</Typography>
	                )}
	            </Box>
	        );

	        return (
	            <Tooltip title={tooltip} arrow>
	                <Box component="span" sx={{display: "inline-flex"}}>
	                    <Chip
	                        size="small"
	                        label={label}
	                        color={meta?.color || "default"}
	                        variant={meta?.variant || "outlined"}
	                        sx={{fontWeight: 600}}
	                    />
	                </Box>
	            </Tooltip>
	        );
	    };

	    useEffect(() => {
	        // initial logs load / reset on action id change
	        setLogs([]);
	        setRunLogs([]);
        setRunLogsPage(0);
        setRunLogsHasNext(false);
        setDoLaterItems([]);
        setDoLaterPage(0);
        setDoLaterHasNext(false);
        setSelectedRunLogId(null);
        setSelectedRunLogs([]);
        setSelectedRunLogsPage(0);
        setSelectedRunLogsHasNext(false);
        setSelectedRunLogsCounts(null);
        setSelectedRunLogsErrorTypeCounts(null);
        setSelectedRunLogsTotal(0);
        if (id) {
            fetchLogsPage(0, {append: false});
            fetchRunLogsPage(0, {append: false});
            fetchDoLaterPage(0, {append: false});
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    // While action is IN_PROGRESS, poll logs/status every 30s
    useEffect(() => {
        if (!id || !isInProgress) return;

        let mounted = true;

        const refresh = async () => {
            setLogsRefreshing(true);
            try {
                const [actionData, logsData, runLogsData, doLaterData] = await Promise.all([
                    getDeferredActionById(id),
                    getDeferredActionLogs(id, 0, LOG_PAGE_SIZE),
                    getDeferredActionRunLogs(id, 0, RUN_LOG_PAGE_SIZE),
                    getDeferredActionDoLater(id, 0, DO_LATER_PAGE_SIZE),
                ]);

                if (!mounted) return;

                if (actionData) {
                    setItem((prev) => {
                        if (!prev) return actionData;
                        return {
                            ...prev,
                            overallStatus: actionData.overallStatus ?? prev.overallStatus,
                        };
                    });
                }

                if (logsData) {
                    const nextLogs = Array.isArray(logsData?.logs) ? logsData.logs : [];
                    // refresh only first page view; keep already loaded older logs after it
                    setLogs((prev) => {
                        if (!prev || prev.length <= LOG_PAGE_SIZE) return nextLogs;
                        return [...nextLogs, ...prev.slice(LOG_PAGE_SIZE)];
                    });
                }

                if (runLogsData) {
                    const nextRunLogs = Array.isArray(runLogsData?.logs) ? runLogsData.logs : [];
                    setRunLogsHasNext(!!runLogsData?.hasNext);
                    setRunLogs((prev) => {
                        if (!prev || prev.length <= RUN_LOG_PAGE_SIZE) return nextRunLogs;
                        return [...nextRunLogs, ...prev.slice(RUN_LOG_PAGE_SIZE)];
                    });
                }
                if (doLaterData) {
                    const nextItems = Array.isArray(doLaterData?.items) ? doLaterData.items : [];
                    setDoLaterHasNext(!!doLaterData?.hasNext);
                    setDoLaterItems((prev) => {
                        if (!prev || prev.length <= DO_LATER_PAGE_SIZE) return nextItems;
                        return [...nextItems, ...prev.slice(DO_LATER_PAGE_SIZE)];
                    });
                }
            } catch (e) {
                // ignore transient errors during polling
            } finally {
                if (mounted) setLogsRefreshing(false);
            }
        };

        // Initial refresh as soon as we enter IN_PROGRESS
        refresh();
        const t = setInterval(refresh, 30_000);

        return () => {
            mounted = false;
            clearInterval(t);
        };
    }, [id, isInProgress, selectedRunLogId]);

    useEffect(() => {
        if (!id || !selectedRunLogId) return;
        fetchSelectedRunLogsPage(selectedRunLogId, 0, {append: false});
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id, selectedRunLogId, isInProgress]);

    const script = item?.script;
    const params = useMemo(() => {
        if (Array.isArray(script?.params)) return script.params;
        return [];
    }, [script?.params]);
    const paramById = useMemo(() => {
        const m = new Map();
        params.forEach(p => m.set(p.id, p));
        return m;
    }, [params]);
    const RUN_TRIGGER_LABEL = {
        MANUAL: "Ручной",
        SCHEDULED: "По расписанию",
        DO_LATER: "Сделать позже",
    };
	    const RUN_TYPE_LABEL = {
	        ACTION_ALL_USERS: "Action для всех пользователей",
	        ACTION_SINGLE_USER: "Action для одного пользователя",
	        ALL_ENABLED_ACTIONS: "Все включенные action",
	    };
	    const RUN_LOG_STATUS_META = {
	        RUNNING: {label: "Выполняется", color: "warning", variant: "filled"},
	        RESUMED: {label: "Возобновлено", color: "info", variant: "filled"},
	        FINISHED: {label: "Завершено", color: "success", variant: "filled"},
	    };
	    const DO_LATER_STATUS_LABEL = {
	        SCHEDULED: "Запланировано",
	        CANCELED: "Отменено",
	        EXECUTED: "Выполнено",
        FAILED: "Ошибка",
    };

    // Разбор templateBody (массив действий)
    let templateBodyArr = [];
    try {
        if (script?.templateBody) {
            const parsed = JSON.parse(script.templateBody);
            if (Array.isArray(parsed)) templateBodyArr = parsed;
        }
    } catch { /* no-op */
    }

    async function handleSaveEditedParam() {
        const {editing, value} = editDlg;
        if (!editing) return;

        await updateDeferredActionParamValue(editing.rowId, value);

        // Оптимистично обновим локально
        setItem(prev => {
            if (!prev) return prev;
            const next = {...prev, paramValues: [...(prev.paramValues || [])]};
            const idx = next.paramValues.findIndex(v => v.id === editing.rowId);
            if (idx >= 0) {
                next.paramValues[idx] = {...next.paramValues[idx], value};
            } else {
                const j = next.paramValues.findIndex(v => v.scriptParamId === editing.scriptParamId);
                if (j >= 0) next.paramValues[j] = {...next.paramValues[j], value};
            }
            return next;
        });

        editDlg.closeDialog();
    }

    const handleRun = async () => {
        if (!window.confirm(`Запустить действие "${item?.name || id}" сейчас?`)) {
            return;
        }
        setLoadingSend(true);
        try {
            await deferredActionsRunById(id);
            // Optimistic UI: action is running now.
            setItem(prev => prev ? ({...prev, overallStatus: "IN_PROGRESS"}) : prev);
            setSelectedRunLogId(null);
            setSelectedRunLogs([]);
            setSelectedRunLogsPage(0);
            setSelectedRunLogsHasNext(false);
            setSelectedRunLogsCounts(null);
            setSelectedRunLogsTotal(0);
            await Promise.all([
                fetchLogsPage(0, {append: false}),
                fetchRunLogsPage(0, {append: false}),
            ]);
            alert("✅ Запуск действия начат");
        } catch (err) {
            console.error(err);
            const status = err?.response?.status;
            const message = err?.response?.data?.message;
            if (status === 400) {
                alert(`❌ ${message || "Действие уже выполняется"}`);
            } else {
                alert("❌ Не удалось запустить действие");
            }
        } finally {
            setLoadingSend(false);
        }
    };

    const handleDelete = async () => {
        if (!item?.id) return;
        if (!window.confirm(`Удалить отложенное действие "${item?.name || item.id}"?\nЛоги и параметры будут удалены. Скрипт будет удален, если больше нигде не используется.`)) {
            return;
        }
        try {
            await deleteDeferredAction(item.id);
            navigate("/deferred-actions");
        } catch (err) {
            console.error(err);
            alert("❌ Не удалось удалить");
        }
    };

    const toggleSection = (key) => {
        setSectionsOpen((prev) => ({...prev, [key]: !prev[key]}));
    };

    const handleCreateDoLater = async () => {
        if (!doLaterAtLocal) {
            alert("❌ Укажите дату и время");
            return;
        }
        if (!/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(doLaterAtLocal)) {
            alert("❌ Некорректная дата/время");
            return;
        }
        if (!window.confirm(`Добавить "Сделать позже" на ${doLaterAtLocal} (МСК)?`)) {
            return;
        }

        setDoLaterSubmitting(true);
        try {
            await createDeferredActionDoLater(id, doLaterAtLocal);
            setDoLaterAtLocal("");
            await fetchDoLaterPage(0, {append: false});
            alert("✅ Добавлено в \"Сделать позже\"");
        } catch (err) {
            console.error(err);
            alert(err?.response?.data?.message || "❌ Не удалось создать \"Сделать позже\"");
        } finally {
            setDoLaterSubmitting(false);
        }
    };

    const handleCancelDoLater = async (doLaterId) => {
        if (!window.confirm(`Отменить "Сделать позже" #${doLaterId}?`)) return;
        try {
            await cancelDeferredActionDoLater(id, doLaterId);
            await fetchDoLaterPage(0, {append: false});
            alert("✅ Отменено");
        } catch (err) {
            console.error(err);
            alert(err?.response?.data?.message || "❌ Не удалось отменить");
        }
    };

    const intervalLabel = useMemo(() => {
        const v = item?.intervalMinutes;
        if (!v || v <= 0) return "Однократно";
        return `Каждые ${v} мин`;
    }, [item?.intervalMinutes]);

    const overallMeta = OVERALL_STATUS[item?.overallStatus || "NOT_STARTED"] || {
        label: String(item?.overallStatus || "NOT_STARTED"),
        color: "default",
        variant: "outlined"
    };

    return (
        <Box>
            <Typography variant="h6" sx={{mb: 0.5}}>Отложенное действие</Typography>
            <Typography variant="body2" sx={{opacity: 0.75, mb: 2}}>
                Управление запуском, историей и отложенными запусками в одном месте.
            </Typography>

            {loading && <CircularProgress size={24}/>}
            {error && <Typography color="error" sx={{mb: 2}}>{error}</Typography>}

            {item && (
                <>
                    <Card sx={{mb: 2}}>
                        <CardContent>
                            <Stack spacing={2}>
                                <Stack
                                    direction={{xs: "column", md: "row"}}
                                    spacing={1}
                                    alignItems={{xs: "flex-start", md: "center"}}
                                    justifyContent="space-between"
                                >
                                    <Box>
                                        <Typography variant="h6">{item.name}</Typography>
                                        <Typography variant="body2" sx={{opacity: 0.75}}>
                                            Скрипт: {script?.title || "Без названия"} •
                                            Создано: {fmtDate(item.createdAt)}
                                        </Typography>
                                    </Box>
                                    <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                                        <Chip label={overallMeta.label} color={overallMeta.color}
                                              variant={overallMeta.variant} size="small"/>
                                        <Chip label={intervalLabel} variant="outlined" size="small"/>
                                        {(item?.intervalMinutes ?? 0) > 0 ? (
                                            <Chip
                                                icon={item.isEnabled ? <CheckCircleIcon/> : <CancelIcon/>}
                                                label={item.isEnabled ? "Включено" : "Выключено"}
                                                color={item.isEnabled ? "success" : "default"}
                                                size="small"
                                            />
                                        ) : null}
                                    </Stack>
                                </Stack>

                                {(item?.intervalMinutes ?? 0) > 0 ? (
                                    <Stack direction="row" alignItems="center" spacing={1}>
                                        <Typography variant="body2">Вкл/Выкл</Typography>
                                        <Tooltip title={item.isEnabled ? "Отключить действие" : "Включить действие"}>
                                            <Switch
                                                checked={item.isEnabled}
                                                color="success"
                                                onChange={async (e) => {
                                                    const newValue = e.target.checked;
                                                    setItem(prev => ({...prev, isEnabled: newValue}));
                                                    try {
                                                        await updateDeferredActionStatus(item.id, newValue);
                                                    } catch (err) {
                                                        setItem(prev => ({...prev, isEnabled: !newValue}));
                                                    }
                                                }}
                                            />
                                        </Tooltip>
                                    </Stack>
                                ) : null}

                                {item.intervalMinutes ? (
                                    <IntervalMinutesField
                                        item={item}
                                        onSave={(val) => {
                                            updateDeferredActionInterval(id, val).then(() => {
                                                setItem({...item, intervalMinutes: val});
                                                alert("Success");
                                            });
                                        }}
                                    />
                                ) : null}

                                <Stack direction={{xs: "column", sm: "row"}} spacing={1.5}>
                                    <Button
                                        variant="contained"
                                        color="success"
                                        startIcon={!loadingSend && <PlayArrowIcon/>}
                                        onClick={handleRun}
                                        disabled={loadingSend || isInProgress}
                                        sx={{textTransform: "none", fontWeight: 600}}
                                    >
                                        {loadingSend ?
                                            <CircularProgress size={22} color="inherit"/> : "Запустить сейчас"}
                                    </Button>
                                    <Button
                                        variant="outlined"
                                        color="primary"
                                        startIcon={<EditIcon/>}
                                        onClick={() => navigate("/scripts/" + script.id + "/update")}
                                        sx={{textTransform: "none", fontWeight: 600}}
                                    >
                                        Редактировать скрипт
                                    </Button>
                                    <Button
                                        variant="outlined"
                                        color="error"
                                        onClick={handleDelete}
                                        sx={{textTransform: "none", fontWeight: 600}}
                                    >
                                        Удалить
                                    </Button>
                                </Stack>
                            </Stack>
                        </CardContent>
                    </Card>


                    <Card sx={{mb: 2}}>
                        <CardContent>
                            <Stack direction="row" justifyContent="space-between" alignItems="center"
                                   sx={{mb: sectionsOpen.scriptParams ? 1 : 0}}>
                                <Typography variant="subtitle1">Технические детали</Typography>
                                <IconButton size="small" onClick={() => toggleSection("scriptParams")}>
                                    {sectionsOpen.scriptParams ? <ExpandLessIcon/> : <ExpandMoreIcon/>}
                                </IconButton>
                            </Stack>

                            {sectionsOpen.scriptParams && (
                                <Stack spacing={2}>
                                    <Typography variant="subtitle2">Параметры скрипта</Typography>
                                    <Table size="small">
                                        <TableHead>
                                            <TableRow>
                                                <TableCell>Имя</TableCell>
                                                <TableCell>Источник</TableCell>
                                                <TableCell>Обяз.</TableCell>
                                                <TableCell>Default</TableCell>
                                                <TableCell>Resolver Path</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {params.map(p => (
                                                <TableRow key={p.id}>
                                                    <TableCell>{p.name}</TableCell>
                                                    <TableCell>
                                                        <Chip size="small" label={p.source}
                                                              color={SOURCE_COLORS[p.source] || "default"}/>
                                                    </TableCell>
                                                    <TableCell>{p.required ? "Да" : "Нет"}</TableCell>
                                                    <TableCell><code>{p.defaultValue === null ? "null" : String(p.defaultValue)}</code></TableCell>
                                                    <TableCell><code>{p.resolverPath || "—"}</code></TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>

                                    <Divider/>

                                    <Typography variant="subtitle2">Значения внешних параметров</Typography>
                                    {item.paramValues?.length ? (
                                        <Table size="small">
                                            <TableHead>
                                                <TableRow>
                                                    <TableCell>Параметр</TableCell>
                                                    <TableCell>scriptParamId</TableCell>
                                                    <TableCell>Значение</TableCell>
                                                    <TableCell width={120}/>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {item.paramValues.map(v => {
                                                    const p = paramById.get(v.scriptParamId);
                                                    return (
                                                        <TableRow key={v.id}>
                                                            <TableCell>{p?.name || "—"}</TableCell>
                                                            <TableCell>{v.scriptParamId}</TableCell>
                                                            <TableCell><code>{String(v.value)}</code></TableCell>
                                                            <TableCell>
                                                                <Tooltip title="Изменить">
                                                                    <IconButton
                                                                        size="small"
                                                                        onClick={() =>
                                                                            editDlg.openDialog({
                                                                                rowId: v.id,
                                                                                scriptParamId: v.scriptParamId,
                                                                                name: p?.name || `param_${v.scriptParamId}`,
                                                                                value: String(v.value ?? ""),
                                                                            })
                                                                        }
                                                                    >
                                                                        <EditIcon fontSize="small"/>
                                                                    </IconButton>
                                                                </Tooltip>
                                                            </TableCell>
                                                        </TableRow>
                                                    );
                                                })}
                                            </TableBody>
                                        </Table>
                                    ) : (
                                        <Typography variant="body2" sx={{opacity: 0.8}}>
                                            Нет значений внешних параметров.
                                        </Typography>
                                    )}

                                    <Divider/>

                                    <Typography variant="subtitle2">Условие (condition)</Typography>
                                    <Typography variant="caption" sx={{opacity: 0.7}}>Читаемое
                                        представление</Typography>
                                    <ConditionReadableView condition={script?.condition}/>
                                    <Typography variant="caption" sx={{opacity: 0.7}}>Raw JSON</Typography>
                                    <TextField
                                        value={script?.condition || ""}
                                        onChange={() => {
                                        }}
                                        fullWidth
                                        multiline
                                        minRows={5}
                                        InputProps={{readOnly: true}}
                                    />

                                    <Divider/>

                                    <Typography variant="subtitle2">Действия (templateBody)</Typography>
                                    {templateBodyArr.length ? (
                                        <Stack spacing={1}>
                                            {templateBodyArr.map((act, idx) => (
                                                <Box key={idx} sx={{
                                                    p: 1,
                                                    borderRadius: 1,
                                                    border: "1px dashed rgba(255,255,255,0.2)"
                                                }}>
                                                    <Stack direction="row" spacing={1} alignItems="center"
                                                           sx={{mb: 0.5}}>
                                                        <Typography variant="body2">
                                                            <b>#{idx + 1}</b> type: <code>{act.type}</code>
                                                        </Typography>
                                                    </Stack>
                                                    <Grid container spacing={1}>
                                                        {act.body && Object.entries(act.body).map(([k, v]) => (
                                                            <Grid item xs={12} md={6} key={k}>
                                                                <Typography variant="body2">
                                                                    <b>{k}:</b> <VarView value={v}/>
                                                                </Typography>
                                                            </Grid>
                                                        ))}
                                                    </Grid>
                                                </Box>
                                            ))}
                                        </Stack>
                                    ) : (
                                        <Typography variant="body2" sx={{opacity: 0.8}}>
                                            Пусто или некорректный JSON.
                                        </Typography>
                                    )}

                                    <Typography variant="caption" sx={{opacity: 0.7}}>Raw JSON</Typography>
                                    <TextField
                                        value={script?.templateBody || ""}
                                        onChange={() => {
                                        }}
                                        fullWidth
                                        multiline
                                        minRows={8}
                                        InputProps={{readOnly: true}}
                                    />
                                </Stack>
                            )}
                        </CardContent>
                    </Card>
                    
                    <Grid container spacing={2}>
                        <Grid item xs={12} lg={8}>
                            <Card sx={{mb: 2}}>
                                <CardContent>
                                    <Stack direction="row" justifyContent="space-between" alignItems="center"
                                           sx={{mb: 1}}>
                                        <Box>
                                            <Typography variant="subtitle1">История запусков</Typography>
                                            <Typography variant="caption" sx={{opacity: 0.75}}>
                                                Нажмите на строку, чтобы открыть детальные логи запуска.
                                            </Typography>
                                        </Box>
                                        <Stack direction="row" spacing={1} alignItems="center">
                                            {logsRefreshing && <CircularProgress size={14}/>}
                                            {logsLoading && <CircularProgress size={14}/>}
                                            {runLogsLoading && <CircularProgress size={16}/>}
                                        </Stack>
                                    </Stack>

                                    {runLogs.length === 0 ? (
                                        <Typography variant="body2" sx={{opacity: 0.7}}>
                                            История запусков отсутствует.
                                        </Typography>
                                    ) : (
                                        <Box sx={{overflowX: "auto"}}>
	                                            <Table size="small">
	                                                <TableHead>
	                                                    <TableRow>
	                                                        <TableCell width={80}>ID</TableCell>
	                                                        <TableCell width={140}>Статус</TableCell>
	                                                        <TableCell width={150}>Триггер</TableCell>
	                                                        <TableCell width={220}>Тип запуска</TableCell>
	                                                        <TableCell width={140}>Сделать позже ID</TableCell>
	                                                        <TableCell width={220}>Кто запустил</TableCell>
	                                                        <TableCell width={200}>Создано</TableCell>
	                                                        <TableCell width={200}>Завершено</TableCell>
	                                                        <TableCell width={120} align="right">Длительность</TableCell>
	                                                    </TableRow>
	                                                </TableHead>
	                                                <TableBody>
	                                                    {runLogs.map((r) => (
	                                                        <TableRow
                                                            key={r.id}
                                                            hover
                                                            onClick={() => handleSelectRunLog(r.id)}
                                                            sx={{
                                                                cursor: "pointer",
                                                                bgcolor: selectedRunLogId === r.id ? "rgba(25,118,210,0.12)" : "inherit",
                                                            }}
	                                                        >
	                                                            <TableCell>{r.id}</TableCell>
	                                                            <TableCell><RunLogStatusChip runLog={r}/></TableCell>
	                                                            <TableCell>{RUN_TRIGGER_LABEL[r.triggerType] || r.triggerType || "—"}</TableCell>
	                                                            <TableCell>{RUN_TYPE_LABEL[r.runType] || r.runType || "—"}</TableCell>
	                                                            <TableCell>{r.doLaterId ?? "—"}</TableCell>
	                                                            <TableCell>{r.startedByAdminName || (r.startedByAdminId ? `ID: ${r.startedByAdminId}` : "SYSTEM")}</TableCell>
	                                                            <TableCell>{fmtDate(r.createdAt)}</TableCell>
	                                                            <TableCell>{r.finishedAt ? fmtDate(r.finishedAt) : "—"}</TableCell>
	                                                            <TableCell align="right">{fmtDurationMs(getRunLogDurationMs(r))}</TableCell>
	                                                        </TableRow>
	                                                    ))}
	                                                </TableBody>
	                                            </Table>

                                            {runLogsHasNext && (
                                                <Box sx={{mt: 1, display: "flex", justifyContent: "center"}}>
                                                    <Button
                                                        variant="outlined"
                                                        onClick={() => fetchRunLogsPage(runLogsPage + 1, {append: true})}
                                                        disabled={runLogsLoading}
                                                        startIcon={runLogsLoading ?
                                                            <CircularProgress size={18}/> : null}
                                                        sx={{textTransform: "none"}}
                                                    >
                                                        {runLogsLoading ? "Загрузка..." : "Показать еще"}
                                                    </Button>
                                                </Box>
                                            )}
                                        </Box>
                                    )}
                                </CardContent>
                            </Card>

	                            <Card sx={{mb: 2}}>
	                                <CardContent>
	                                    <Stack direction="row" justifyContent="space-between" alignItems="center"
	                                           sx={{mb: 1}}>
	                                        <Box>
	                                            <Typography variant="subtitle1">
	                                                {selectedRunLogId ? `Логи запуска #${selectedRunLogId}` : "Логи выбранного запуска"}
	                                            </Typography>
	                                            {selectedRunLogId && selectedRunLog && (
	                                                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
	                                                    <RunLogStatusChip runLog={selectedRunLog}/>
	                                                    <Typography variant="caption" sx={{opacity: 0.8}}>
	                                                        Создано: {fmtDate(selectedRunLog.createdAt)}
	                                                    </Typography>
	                                                    {selectedRunLog.resumedAt && (
	                                                        <Typography variant="caption" sx={{opacity: 0.8}}>
	                                                            Возобновлено: {fmtDate(selectedRunLog.resumedAt)}
	                                                        </Typography>
	                                                    )}
	                                                    {selectedRunLog.finishedAt && (
	                                                        <Typography variant="caption" sx={{opacity: 0.8}}>
	                                                            Завершено: {fmtDate(selectedRunLog.finishedAt)}
	                                                        </Typography>
	                                                    )}
	                                                    <Typography variant="caption" sx={{opacity: 0.8}}>
	                                                        Длительность: {fmtDurationMs(getRunLogDurationMs(selectedRunLog))}
	                                                    </Typography>
	                                                </Stack>
	                                            )}
	                                        </Box>
	                                        {selectedRunLogsLoading && <CircularProgress size={16}/>}
	                                    </Stack>

                                    {!selectedRunLogId ? (
                                        <Typography variant="body2" sx={{opacity: 0.75}}>
                                            Выберите запуск в таблице выше, чтобы открыть его логи.
                                        </Typography>
                                    ) : selectedRunLogsLoading ? (
                                        <Stack direction="row" spacing={1} alignItems="center">
                                            <CircularProgress size={16}/>
                                            <Typography variant="body2" sx={{opacity: 0.75}}>
                                                Загружаем логи запуска #{selectedRunLogId}...
                                            </Typography>
                                        </Stack>
                                    ) : (
                                        <DeferredActionLogList
                                            logs={selectedRunLogs}
                                            statusCounts={selectedRunLogsCounts}
                                            errorTypeCounts={selectedRunLogsErrorTypeCounts}
                                            total={selectedRunLogsTotal}
                                            hasNext={selectedRunLogsHasNext}
                                            loading={selectedRunLogsLoading}
                                            onLoadMore={() => fetchSelectedRunLogsPage(selectedRunLogId, selectedRunLogsPage + 1, {append: true})}
                                        />
                                    )}
                                </CardContent>
                            </Card>
                        </Grid>

                        <Grid item xs={12} lg={4}>
                            <Card sx={{mb: 2}}>
                                <CardContent>
                                    <Stack direction="row" justifyContent="space-between" alignItems="center"
                                           sx={{mb: sectionsOpen.doLater ? 1 : 0}}>
                                        <Typography variant="subtitle1">Сделать позже</Typography>
                                    </Stack>


                                    <>
                                        <Alert severity="info" sx={{mb: 1.5}}>
                                            Время задается только по Москве (МСК).
                                        </Alert>
                                        <Stack direction={{xs: "column", md: "row"}} spacing={1} sx={{mb: 1.5}}>
                                            <TextField
                                                size="small"
                                                label="Дата и время (МСК)"
                                                type="datetime-local"
                                                value={doLaterAtLocal}
                                                onChange={(e) => setDoLaterAtLocal(e.target.value)}
                                                InputLabelProps={{shrink: true}}
                                            />
                                            <Button
                                                variant="contained"
                                                onClick={handleCreateDoLater}
                                                disabled={doLaterSubmitting}
                                                startIcon={doLaterSubmitting ?
                                                    <CircularProgress size={16} color="inherit"/> : null}
                                                sx={{textTransform: "none"}}
                                            >
                                                {doLaterSubmitting ? "Добавляем..." : "Добавить"}
                                            </Button>
                                        </Stack>

                                        {doLaterItems.length === 0 ? (
                                            <Typography variant="body2" sx={{opacity: 0.75}}>
                                                Список пуст.
                                            </Typography>
                                        ) : (
                                            <Box sx={{overflowX: "auto"}}>
                                                <Table size="small">
                                                    <TableHead>
                                                        <TableRow>
                                                            <TableCell width={70}>ID</TableCell>
                                                            <TableCell width={170}>Выполнить в</TableCell>
                                                            <TableCell width={110}>Статус</TableCell>
                                                            <TableCell width={170}>Кто создал</TableCell>
                                                            <TableCell width={120}>Запуск</TableCell>
                                                            <TableCell width={100}/>
                                                        </TableRow>
                                                    </TableHead>
                                                    <TableBody>
                                                        {doLaterItems.map((d) => (
                                                            <TableRow key={d.id}>
                                                                <TableCell>{d.id}</TableCell>
                                                                <TableCell>{fmtDateMsk(d.fireAt)}</TableCell>
                                                                <TableCell>{DO_LATER_STATUS_LABEL[d.status] || d.status}</TableCell>
                                                                <TableCell>{d.createdByAdminName || (d.createdByAdminId ? `ID: ${d.createdByAdminId}` : "—")}</TableCell>
                                                                <TableCell>{d.runLogId ?? "—"}</TableCell>
                                                                <TableCell>
                                                                    {d.status === "SCHEDULED" ? (
                                                                        <Button
                                                                            size="small"
                                                                            color="error"
                                                                            onClick={() => handleCancelDoLater(d.id)}
                                                                            sx={{textTransform: "none"}}
                                                                        >
                                                                            Отменить
                                                                        </Button>
                                                                    ) : "—"}
                                                                </TableCell>
                                                            </TableRow>
                                                        ))}
                                                    </TableBody>
                                                </Table>
                                                {doLaterHasNext && (
                                                    <Box sx={{mt: 1, display: "flex", justifyContent: "center"}}>
                                                        <Button
                                                            variant="outlined"
                                                            onClick={() => fetchDoLaterPage(doLaterPage + 1, {append: true})}
                                                            disabled={doLaterLoading}
                                                            startIcon={doLaterLoading ?
                                                                <CircularProgress size={18}/> : null}
                                                            sx={{textTransform: "none"}}
                                                        >
                                                            {doLaterLoading ? "Загрузка..." : "Показать еще"}
                                                        </Button>
                                                    </Box>
                                                )}
                                            </Box>
                                        )}
                                    </>

                                </CardContent>
                            </Card>
                        </Grid>
                    </Grid>
                </>
            )}

            {/* Dialog редактирования одного значения */}
            <Dialog open={editDlg.open} onClose={editDlg.closeDialog} fullWidth maxWidth="sm">
                <DialogTitle>Изменить значение</DialogTitle>
                <DialogContent>
                    <Stack spacing={1} sx={{mt: 1}}>
                        <Typography variant="body2" sx={{opacity: 0.8}}>
                            Параметр: <b>{editDlg.editing?.name}</b>
                        </Typography>
                        <TextField
                            autoFocus
                            label="Значение"
                            value={editDlg.value}
                            onChange={(e) => editDlg.setValue(e.target.value)}
                            fullWidth
                            size="small"
                        />
                    </Stack>
                </DialogContent>
                <DialogActions sx={{p: 2}}>
                    <Button onClick={editDlg.closeDialog}>Отмена</Button>
                    <Button variant="contained" onClick={handleSaveEditedParam}>
                        Сохранить
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}
